import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';

import type { Notification } from '@/api/notifications.api';
import { cn } from '@/lib/cn';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
} from '@/hooks/useNotifications';
import { notificationKindPresentation, relativeTime, sortUnreadFirst } from './notificationFormat';

/**
 * NotificationInbox — the compact header-bell dropdown panel
 * (Foundations 2026-07-21). Self-contained: it owns its own data
 * fetching + mutations, so the header only has to render it inside a
 * popover. Clicking a row marks it read and navigates to its `linkPath`.
 *
 * The full-page counterpart is `src/pages/NotificationsPage.tsx`; both
 * share the `NotificationRow` presentation below.
 */

const DEFAULT_PANEL_LIMIT = 8;

export interface NotificationInboxProps {
  /** How many rows the panel shows. Default 8. */
  limit?: number;
  /** Called after a row navigates — e.g. to close the popover. */
  onNavigate?: () => void;
  className?: string;
}

export function NotificationInbox({
  limit = DEFAULT_PANEL_LIMIT,
  onNavigate,
  className,
}: NotificationInboxProps) {
  const navigate = useNavigate();
  const listQuery = useNotificationList({ limit });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = listQuery.data ? sortUnreadFirst(listQuery.data.items) : [];
  const hasUnread = items.some((n) => n.readAt === null);

  function handleActivate(n: Notification) {
    if (n.readAt === null) markRead.mutate(n.id);
    if (n.linkPath) {
      void navigate(n.linkPath);
      onNavigate?.();
    }
  }

  return (
    <div
      className={cn(
        'flex w-80 flex-col overflow-hidden rounded-kw-lg border border-kw-border bg-white shadow-kw-md',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-kw-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Bell className="size-4 text-kw-cerulean" />
          <h2 className="text-[13px] font-semibold text-kw-shark">Notifications</h2>
        </div>
        <button
          type="button"
          disabled={!hasUnread || markAll.isPending}
          onClick={() => {
            markAll.mutate();
          }}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-kw-cerulean hover:underline disabled:cursor-not-allowed disabled:text-kw-ghost disabled:no-underline"
        >
          <CheckCheck className="size-3.5" />
          Mark all read
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {listQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <div className="size-6 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
          </div>
        ) : listQuery.error ? (
          <p className="px-4 py-8 text-center text-[13px] text-kw-danger-text">
            Couldn&apos;t load notifications.
          </p>
        ) : items.length === 0 ? (
          <EmptyInbox />
        ) : (
          <ul className="divide-y divide-kw-border">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onActivate={() => {
                  handleActivate(n);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmptyInbox() {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      <Bell className="size-7 text-kw-ghost" />
      <p className="text-[13px] text-kw-muted">You&apos;re all caught up.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared row (reused by NotificationsPage)
// ─────────────────────────────────────────────────────────────────────────

export function NotificationRow({
  notification,
  onActivate,
}: {
  notification: Notification;
  onActivate: () => void;
}) {
  const unread = notification.readAt === null;
  const { icon: KindIcon, label: kindLabel } = notificationKindPresentation(notification.kind);
  return (
    <li>
      <button
        type="button"
        onClick={onActivate}
        className={cn(
          'flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-kw-bg',
          unread && 'bg-kw-cerulean-light/40',
        )}
      >
        <span
          aria-hidden
          className={cn('mt-1.5 size-2 shrink-0 rounded-full', unread ? 'bg-kw-cerulean' : 'bg-transparent')}
        />
        <span title={kindLabel} className="mt-0.5 shrink-0">
          <KindIcon aria-hidden className={cn('size-4', unread ? 'text-kw-cerulean' : 'text-kw-muted')} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-[13px]',
              unread ? 'font-semibold text-kw-shark' : 'text-kw-shark',
            )}
          >
            {notification.title}
          </span>
          {notification.body ? (
            <span className="mt-0.5 block truncate text-[12px] text-kw-muted">{notification.body}</span>
          ) : null}
          <span className="mt-0.5 block text-[11px] text-kw-ghost">
            {relativeTime(notification.createdAt)}
          </span>
        </span>
      </button>
    </li>
  );
}
